import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ImageList,
  ImageListItem,
  TextField,
  Pagination,
  Autocomplete,
} from "@mui/material";
import { tokens } from "../../theme";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import ImageIcon from "@mui/icons-material/Image";
import BusinessIcon from "@mui/icons-material/Business";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import DescriptionIcon from "@mui/icons-material/Description";
import DrawIcon from "@mui/icons-material/Draw";
import Divider from "@mui/material/Divider";
import { base_url } from "../../config/config";

const API_BASE = `${base_url}/v1/simple-app`;
const API_MAIN = `${base_url}/v1`;

const ActivityLogs = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [users, setUsers] = useState([]);
  const [firmas, setFirmas] = useState([]);
  const [lieferants, setLieferants] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [detailDialog, setDetailDialog] = useState({ open: false, log: null });
  const [editDialog, setEditDialog] = useState({ open: false, log: null });
  const [editData, setEditData] = useState({
    description: "",
    lagerLocation: "",
    firmaId: null,
    lieferantId: null,
  });
  const [saving, setSaving] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    startDate: "",
    endDate: "",
  });

  useEffect(() => {
    fetchUsers();
    fetchFirmas();
    fetchLieferants();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [selectedUser, page, dateFilter]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE}/users`);
      const data = await response.json();
      if (data.success) {
        setUsers(data.data);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchFirmas = async () => {
    try {
      const response = await fetch(`${API_MAIN}/getFirma`);
      const data = await response.json();
      setFirmas(data.data || data || []);
    } catch (err) {
      console.error("Error fetching firmas:", err);
    }
  };

  const fetchLieferants = async () => {
    try {
      const response = await fetch(`${API_MAIN}/getLieferant`);
      const data = await response.json();
      setLieferants(data.data || data || []);
    } catch (err) {
      console.error("Error fetching lieferants:", err);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "20",
      });

      if (selectedUser) {
        params.append("userId", selectedUser);
      }
      if (dateFilter.startDate) {
        params.append("startDate", dateFilter.startDate);
      }
      if (dateFilter.endDate) {
        params.append("endDate", dateFilter.endDate);
      }

      const response = await fetch(`${API_BASE}/logs?${params}`);
      const data = await response.json();

      if (data.success) {
        setLogs(data.data);
        setPagination(data.pagination);
      }
    } catch (err) {
      setError("Fehler beim Laden der Aktivitäten");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (log) => {
    setDetailDialog({ open: true, log });
  };

  const handleCloseDetails = () => {
    setDetailDialog({ open: false, log: null });
  };

  const handleOpenEdit = (log) => {
    setEditData({
      description: log.description || "",
      lagerLocation: log.lagerLocation || "",
      firmaId: log.firma?._id || null,
      lieferantId: log.lieferant?._id || null,
    });
    setEditDialog({ open: true, log });
  };

  const handleCloseEdit = () => {
    setEditDialog({ open: false, log: null });
    setEditData({
      description: "",
      lagerLocation: "",
      firmaId: null,
      lieferantId: null,
    });
  };

  const handleSaveEdit = async () => {
    if (!editDialog.log) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/logs/${editDialog.log._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editData),
      });
      const data = await response.json();
      if (data.success) {
        fetchLogs();
        handleCloseEdit();
      } else {
        setError(data.message || "Fehler beim Speichern");
      }
    } catch (err) {
      setError("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActionColor = (actionType) => {
    switch (actionType) {
      case "delivery":
        return "primary";
      case "incoming":
        return "success";
      case "lager":
        return "warning";
      case "posting":
        return "info";
      default:
        return "default";
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" color={colors.grey[100]}>
          Aktivitätsprotokoll
        </Typography>

        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Benutzer filtern</InputLabel>
            <Select
              value={selectedUser}
              label="Benutzer filtern"
              onChange={(e) => {
                setSelectedUser(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">Alle Benutzer</MenuItem>
              {users.map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  {user.displayName || user.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            type="date"
            size="small"
            label="Von"
            value={dateFilter.startDate}
            onChange={(e) => {
              setDateFilter({ ...dateFilter, startDate: e.target.value });
              setPage(1);
            }}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            type="date"
            size="small"
            label="Bis"
            value={dateFilter.endDate}
            onChange={(e) => {
              setDateFilter({ ...dateFilter, endDate: e.target.value });
              setPage(1);
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[400] }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Datum/Zeit</TableCell>
                  <TableCell>Benutzer</TableCell>
                  <TableCell>Aktion</TableCell>
                  <TableCell>Firma/Lieferant</TableCell>
                  <TableCell>Beschreibung</TableCell>
                  <TableCell>Bilder</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color={colors.grey[300]} py={4}>
                        Keine Aktivitäten gefunden.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log._id}>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(log.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="bold">
                          {log.userName || log.userId?.displayName || log.userId?.name || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.actionLabel}
                          color={getActionColor(log.actionType)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {log.firma ? (
                          <Chip
                            icon={<BusinessIcon />}
                            label={log.firma.bezeichnung || log.firma.kurzbezeichnung}
                            size="small"
                            variant="outlined"
                            sx={{ mb: 0.5 }}
                          />
                        ) : null}
                        {log.lieferant ? (
                          <Chip
                            icon={<LocalShippingIcon />}
                            label={log.lieferant.bezeichnung || log.lieferant.kurzbezeichnung}
                            size="small"
                            variant="outlined"
                            color="secondary"
                          />
                        ) : null}
                        {!log.firma && !log.lieferant && (
                          <Typography variant="body2" color={colors.grey[500]}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 150,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {log.description || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {log.images?.length > 0 ? (
                          <Chip
                            icon={<ImageIcon />}
                            label={log.images.length}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => handleViewDetails(log)}
                          color="primary"
                          size="small"
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleOpenEdit(log)}
                          color="secondary"
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination.pages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={pagination.pages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="secondary"
              />
            </Box>
          )}
        </>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        {detailDialog.log && (
          <>
            <DialogTitle>
              Aktivitätsdetails - {detailDialog.log.actionLabel}
            </DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2} mt={1}>
                <Box display="flex" gap={4} flexWrap="wrap">
                  <Box>
                    <Typography variant="body2" color={colors.grey[300]}>
                      Benutzer
                    </Typography>
                    <Typography fontWeight="bold">
                      {detailDialog.log.userName ||
                        detailDialog.log.userId?.displayName ||
                        detailDialog.log.userId?.name}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color={colors.grey[300]}>
                      Datum/Zeit
                    </Typography>
                    <Typography>
                      {formatDate(detailDialog.log.createdAt)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color={colors.grey[300]}>
                      Aktionstyp
                    </Typography>
                    <Chip
                      label={detailDialog.log.actionType}
                      size="small"
                      color={getActionColor(detailDialog.log.actionType)}
                    />
                  </Box>
                </Box>

                {detailDialog.log.firma && (
                  <Box>
                    <Typography variant="body2" color={colors.grey[300]}>
                      Firma
                    </Typography>
                    <Typography>{detailDialog.log.firma.bezeichnung}</Typography>
                  </Box>
                )}

                {detailDialog.log.lieferant && (
                  <Box>
                    <Typography variant="body2" color={colors.grey[300]}>
                      Lieferant
                    </Typography>
                    <Typography>{detailDialog.log.lieferant.bezeichnung}</Typography>
                  </Box>
                )}

                {detailDialog.log.lagerLocation && (
                  <Box>
                    <Typography variant="body2" color={colors.grey[300]}>
                      Lagerort
                    </Typography>
                    <Typography>{detailDialog.log.lagerLocation}</Typography>
                  </Box>
                )}

                <Box>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Beschreibung
                  </Typography>
                  <Typography>
                    {detailDialog.log.description || "Keine Beschreibung"}
                  </Typography>
                </Box>

                {detailDialog.log.images?.length > 0 && (
                  <Box>
                    <Typography variant="body2" color={colors.grey[300]} mb={1}>
                      Bilder ({detailDialog.log.images.length})
                    </Typography>
                    <ImageList cols={3} rowHeight={200}>
                      {detailDialog.log.images.map((image, index) => (
                        <ImageListItem key={index}>
                          <img
                            src={image.s3Url}
                            alt={`Bild ${index + 1}`}
                            loading="lazy"
                            style={{
                              objectFit: "cover",
                              width: "100%",
                              height: "100%",
                              borderRadius: 8,
                              cursor: "pointer",
                            }}
                            onClick={() => window.open(image.s3Url, "_blank")}
                          />
                        </ImageListItem>
                      ))}
                    </ImageList>
                  </Box>
                )}

                {/* Documents with Categories */}
                {detailDialog.log.documents?.length > 0 && (
                  <Box>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" color={colors.grey[300]} mb={1}>
                      <DescriptionIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "text-bottom" }} />
                      Dokumente ({detailDialog.log.documents.length})
                    </Typography>
                    <ImageList cols={3} rowHeight={200}>
                      {detailDialog.log.documents.map((doc, index) => (
                        <ImageListItem key={index} sx={{ position: "relative" }}>
                          <img
                            src={doc.image?.s3Url}
                            alt={`Dokument ${index + 1}`}
                            loading="lazy"
                            style={{
                              objectFit: "cover",
                              width: "100%",
                              height: "100%",
                              borderRadius: 8,
                              cursor: "pointer",
                            }}
                            onClick={() => window.open(doc.image?.s3Url, "_blank")}
                          />
                          {(doc.categoryName || doc.category?.name) && (
                            <Chip
                              label={doc.categoryName || doc.category?.name}
                              size="small"
                              color="primary"
                              sx={{
                                position: "absolute",
                                bottom: 8,
                                left: 8,
                                fontSize: "0.7rem",
                              }}
                            />
                          )}
                        </ImageListItem>
                      ))}
                    </ImageList>
                  </Box>
                )}

                {/* Fotofeatures */}
                {(detailDialog.log.fotoFeatures?.text || detailDialog.log.fotoFeatures?.category) && (
                  <Box>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" color={colors.grey[300]}>
                      Fotofeatures
                    </Typography>
                    {detailDialog.log.fotoFeatures.text && (
                      <Typography>{detailDialog.log.fotoFeatures.text}</Typography>
                    )}
                    {detailDialog.log.fotoFeatures.category && (
                      <Chip
                        label={detailDialog.log.fotoFeatures.category.name || detailDialog.log.fotoFeatures.category}
                        size="small"
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Box>
                )}

                {/* Versand Section */}
                {(detailDialog.log.versenderName || detailDialog.log.verladungImages?.length > 0 || detailDialog.log.dokumentFotos?.length > 0) && (
                  <Box>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h6" color={colors.greenAccent[400]} mb={1}>
                      Versand
                    </Typography>

                    {detailDialog.log.versenderName && (
                      <Box mb={1}>
                        <Typography variant="body2" color={colors.grey[300]}>
                          Versender Name
                        </Typography>
                        <Typography>{detailDialog.log.versenderName}</Typography>
                      </Box>
                    )}

                    {detailDialog.log.verladungImages?.length > 0 && (
                      <Box mb={1}>
                        <Typography variant="body2" color={colors.grey[300]} mb={1}>
                          Verladung & Ladungssicherung ({detailDialog.log.verladungImages.length})
                        </Typography>
                        <ImageList cols={3} rowHeight={200}>
                          {detailDialog.log.verladungImages.map((image, index) => (
                            <ImageListItem key={index}>
                              <img
                                src={image.s3Url}
                                alt={`Verladung ${index + 1}`}
                                loading="lazy"
                                style={{
                                  objectFit: "cover",
                                  width: "100%",
                                  height: "100%",
                                  borderRadius: 8,
                                  cursor: "pointer",
                                }}
                                onClick={() => window.open(image.s3Url, "_blank")}
                              />
                            </ImageListItem>
                          ))}
                        </ImageList>
                      </Box>
                    )}

                    {detailDialog.log.dokumentFotos?.length > 0 && (
                      <Box>
                        <Typography variant="body2" color={colors.grey[300]} mb={1}>
                          Dokument-Fotos ({detailDialog.log.dokumentFotos.length})
                        </Typography>
                        <ImageList cols={3} rowHeight={200}>
                          {detailDialog.log.dokumentFotos.map((image, index) => (
                            <ImageListItem key={index}>
                              <img
                                src={image.s3Url}
                                alt={`Dokument-Foto ${index + 1}`}
                                loading="lazy"
                                style={{
                                  objectFit: "cover",
                                  width: "100%",
                                  height: "100%",
                                  borderRadius: 8,
                                  cursor: "pointer",
                                }}
                                onClick={() => window.open(image.s3Url, "_blank")}
                              />
                            </ImageListItem>
                          ))}
                        </ImageList>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Approval / Signature Section */}
                {(detailDialog.log.approverName || detailDialog.log.signatureImage) && (
                  <Box>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h6" color={colors.greenAccent[400]} mb={1}>
                      <DrawIcon sx={{ fontSize: 20, mr: 0.5, verticalAlign: "text-bottom" }} />
                      Freigabe / Unterschrift
                    </Typography>

                    {detailDialog.log.approverName && (
                      <Box mb={1}>
                        <Typography variant="body2" color={colors.grey[300]}>
                          Freigebender
                        </Typography>
                        <Typography fontWeight="bold">{detailDialog.log.approverName}</Typography>
                      </Box>
                    )}

                    {detailDialog.log.signatureImage && (
                      <Box>
                        <Typography variant="body2" color={colors.grey[300]} mb={1}>
                          Unterschrift
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: "#fff",
                            borderRadius: 2,
                            p: 1,
                            display: "inline-block",
                            cursor: "pointer",
                          }}
                          onClick={() => window.open(detailDialog.log.signatureImage.s3Url, "_blank")}
                        >
                          <img
                            src={detailDialog.log.signatureImage.s3Url}
                            alt="Unterschrift"
                            style={{
                              maxWidth: 300,
                              maxHeight: 150,
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetails}>Schließen</Button>
              <Button
                onClick={() => {
                  handleCloseDetails();
                  handleOpenEdit(detailDialog.log);
                }}
                variant="contained"
                color="secondary"
              >
                Bearbeiten
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={handleCloseEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Aktivität bearbeiten</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Autocomplete
              options={firmas}
              getOptionLabel={(option) => option.bezeichnung || option.kurzbezeichnung || ""}
              value={firmas.find((f) => f._id === editData.firmaId) || null}
              onChange={(e, newValue) => {
                setEditData({ ...editData, firmaId: newValue?._id || null });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Firma" placeholder="Firma auswählen..." />
              )}
            />

            <Autocomplete
              options={lieferants}
              getOptionLabel={(option) => option.bezeichnung || option.kurzbezeichnung || ""}
              value={lieferants.find((l) => l._id === editData.lieferantId) || null}
              onChange={(e, newValue) => {
                setEditData({ ...editData, lieferantId: newValue?._id || null });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Lieferant" placeholder="Lieferant auswählen..." />
              )}
            />

            <TextField
              label="Beschreibung"
              value={editData.description}
              onChange={(e) =>
                setEditData({ ...editData, description: e.target.value })
              }
              multiline
              rows={3}
              fullWidth
            />

            <TextField
              label="Lagerort"
              value={editData.lagerLocation}
              onChange={(e) =>
                setEditData({ ...editData, lagerLocation: e.target.value })
              }
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Abbrechen</Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            color="secondary"
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : "Speichern"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActivityLogs;
